<template>
  <section class="users settings-grid">
    <slot name="menu"></slot>

    <div
      ref="container"
      class="settings-grid__main custom-scrollbar"
    >
      <div class="settings-grid__main__header">
        <slot name="title"></slot>

        <button
          class="btn btn-secondary btn-icon"
          @click="addUser()"
        >
          <IconPlus size="xs" />
          <span> Criar novo usuário </span>
        </button>
      </div>

      <Loader
        v-if="loading"
        color="#3E9AA6"
      />

      <CustomTable
        v-else-if="usersForTable.length > 0"
        id="users-list"
        :cols="tableHeaders"
        :list="usersForTable"
      >
        <template #actions="{ row: user }">
          <div class="column-actions">
            <button @click.stop="toggleActions(user.id)">
              <IconDotsVert />
            </button>
            <ul
              v-show="openedActions === user.id"
              class="column-actions--dropdown"
            >
              <li>
                <button @click.stop="showEditModal(user.id)">
                  Editar usuário
                </button>
              </li>
              <li v-if="user.blocked">
                <button @click.stop="unblockUser(user.id)">
                  Desbloquear usuário
                </button>
              </li>
              <li v-if="!isLoggedUser(user)">
                <button
                  class="text-danger"
                  @click.stop="showDeleteModal(user)"
                >
                  Excluir usuário
                </button>
              </li>
            </ul>
          </div>
        </template>
      </CustomTable>

      <div
        v-else
        class="settings-grid__empty-state"
      >
        Você ainda não criou nenhum usuário 😿
      </div>
    </div>

    <div
      v-show="creatingUser || userBeingEdited"
      class="edit-modal"
      @click.self="closeModal"
    >
      <div class="edit-modal__content">
        <div
          class="btn-close"
          title="Fechar modal"
          @click="closeModal"
        >
          <IconClose />
        </div>

        <div class="edit-modal__header mb-4">
          <h2>{{ currentAction }} usuário</h2>
        </div>

        <form
          class="users-form"
          @submit.prevent="saveUser"
        >
          <div
            class="form-section"
            :class="{
              'field--error': !isValidName && isDirtyField('userName'),
            }"
          >
            <label for="userName">Nome</label>
            <input
              id="userName"
              v-model.trim="userName"
              name="userName"
              type="text"
              class="form-control"
              autocomplete="off"
              @blur="setDirty('userName')"
              @focus="removeError('userName')"
            />
            <span class="field--warning">
              Preencha ao menos 3 caracteres.
            </span>
          </div>

          <div
            class="form-section"
            :class="{
              'field--error': !isValidEmail && isDirtyField('userEmail'),
            }"
          >
            <label for="userEmail">E-mail</label>
            <input
              id="userEmail"
              v-model.trim="userEmail"
              name="userEmail"
              type="email"
              class="form-control"
              :disabled="emailDisabled"
              @blur="setDirty('userEmail')"
              @focus="removeError('userEmail')"
            />
            <span class="field--warning">
              O e-mail digitado não é válido.
            </span>
          </div>

          <div class="form-section">
            <label for="userRole">Nível de permissão</label>
            <div class="icon-wrapper">
              <span class="slb-tooltip">
                O usuário administrador é o único que pode criar e excluir
                usuários.
              </span>
              <IconHelp
                size="xs"
                color="#474747"
              />
            </div>
            <v-select
              id="userRole"
              v-model="userRole"
              :options="roleOptions"
              :clearable="false"
              :reduce="(opt) => opt.value"
              @wheel.native.stop
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>

          <div class="users-form__buttons">
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              class="btn btn-secondary"
              :disabled="!isValidFields"
              type="submit"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>

          <div class="submit-feedback">{{ submitError }}</div>
        </form>
      </div>
    </div>

    <div
      v-show="userBeingDeleted"
      class="remove-modal"
      @click.self="closeModal"
    >
      <div class="remove-modal__content">
        <div
          class="btn-close"
          title="Fechar modal"
          @click="closeModal"
        >
          <IconClose />
        </div>

        <div class="remove-modal__header">
          <h2>Tem certeza que deseja excluir este usuário?</h2>
        </div>

        <div class="remove-modal__buttons">
          <button
            class="btn btn-neutral"
            type="button"
            @click="closeModal"
          >
            Voltar
          </button>
          <button
            class="btn btn-danger"
            type="submit"
            @click="deleteUser"
          >
            {{ saving ? 'Excluindo...' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import vSelect from 'vue-select';

import CustomTable from '@/components/CustomTable.vue';
import Loader from '@/components/Loader.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import IconDotsVert from '@/components/icons/IconDotsVert.vue';
import IconHelp from '@/components/icons/IconHelp.vue';
import emailValidator from '@/validation/validators/email';
import errosLogin from '@/helpers/errorsLogin';

export default {
  name: 'Users',
  components: {
    CustomTable,
    IconClose,
    IconPlus,
    IconDotsVert,
    IconHelp,
    Loader,
    'v-select': vSelect,
  },
  data() {
    return {
      openedActions: null,
      userBeingEdited: null,
      userBeingDeleted: null,
      creatingUser: false, // TODO: refact
      userName: '',
      userEmail: '',
      userRole: 'manager', // initial
      emailDisabled: false,
      dirtyFields: [],
      saving: false,
      submitError: '',
      roles: {
        admin: 'Administração',
        manager: 'Edição e publicação',
        viewer: 'Somente leitura',
      },
    };
  },
  computed: {
    tableHeaders() {
      const tableHeaders = [
        {
          name: 'name',
          label: 'Nome',
        },
        {
          name: 'email',
          label: 'Email',
        },
        {
          name: 'role',
          label: 'Permissão',
          maxWidth: '200px',
        },
        {
          name: 'actions',
          label: 'Ações',
          maxWidth: '100px',
          isSortable: false,
        },
      ];

      return tableHeaders;
    },
    isAdminList() {
      return this.$route.name === 'admin';
    },
    usersForTable() {
      const users = this.isAdminList ? this.dashboard.globalUsers : this.users;

      return users.map((user) => ({
        ...user,
        role: this.roles[user.role],
      }));
    },
    roleOptions() {
      return Object.entries(this.roles).map(([role, label]) => ({
        value: role,
        label,
      }));
    },
    isValidName() {
      return /\S{3}/.test(this.userName);
    },
    isValidEmail() {
      return emailValidator(this.userEmail);
    },
    isValidFields() {
      return this.isValidName && this.isValidEmail;
    },
    currentAction() {
      return this.userBeingEdited === null ? 'Criar' : 'Editar';
    },
    ...mapState(['loading', 'users', 'dashboard', 'loggedUser', 'customer']),
  },
  methods: {
    isLoggedUser(user) {
      return this.$store.state.loggedUser.email === user.email;
    },
    closeActions() {
      this.openedActions = null;
    },
    toggleActions(id) {
      if (this.openedActions === id) {
        this.openedActions = null;
      } else {
        this.openedActions = id;
      }
    },
    showEditModal(userId) {
      const users = this.isAdminList ? this.dashboard.globalUsers : this.users;
      const userToEdit = users.find((user) => user.id === userId);

      this.closeActions();
      this.userBeingEdited = userToEdit;
      this.userName = userToEdit.name;
      this.userEmail = userToEdit.email;
      this.userRole = userToEdit.role;
      this.emailDisabled = true;
    },
    showDeleteModal(user) {
      this.closeActions();
      this.userBeingDeleted = user;
    },
    setDirty(field) {
      if (!this.dirtyFields.includes(field)) {
        this.dirtyFields.push(field);
      }
    },
    isDirtyField(field) {
      return this.dirtyFields.includes(field);
    },
    removeError(field) {
      this.dirtyFields = this.dirtyFields.filter((item) => {
        return field !== item;
      });
    },
    addUser() {
      this.submitError = '';
      this.creatingUser = true;
    },
    async saveUser() {
      this.submitError = '';

      if (!this.isValidFields) {
        this.setDirty('name');
        this.setDirty('email');
        return;
      }

      this.saving = true;

      const userBody = {
        name: this.userName,
        email: this.userEmail,
        role: this.userRole,
      };

      if (this.isAdminList) {
        userBody.global = true;
      } else {
        userBody.customer = this.customer;
      }

      let action;

      if (this.userBeingEdited) {
        action = 'updateUser';
        userBody.id = this.userBeingEdited.id;
      } else {
        action = 'createUser';
      }

      this.$store
        .dispatch(action, userBody)
        .then(async () => {
          await this.refreshUsers();
          this.closeModal();
        })
        .catch((err) => {
          this.submitError = errosLogin(err.response?.data?.message);
        });

      this.saving = false;
    },
    async refreshUsers() {
      await this.$store.dispatch('loadUsers', { global: this.isAdminList });
    },
    closeModal() {
      this.creatingUser = false;
      this.userBeingEdited = null;
      this.userBeingDeleted = null;
      this.userName = '';
      this.userEmail = '';
      this.userRole = 'manager';
      this.emailDisabled = false;
      this.dirtyFields = [];
    },
    async deleteUser() {
      this.saving = true;

      await this.$store.dispatch('removeUser', this.userBeingDeleted.id);
      await this.refreshUsers();

      this.saving = false;
      this.closeModal();
    },
    async unblockUser(id) {
      this.saving = true;

      await this.$store.dispatch('unblockUser', id);
      await this.refreshUsers();
      this.closeActions();

      this.saving = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.users {
  .settings-grid {
    &__main__header {
      &::after {
        display: none; // hides the fade
      }
    }

    &__empty-state {
      margin-top: 50px;
      font-size: $size-normal;
      text-align: center;
    }
  }

  #users-list {
    width: 100%;
    height: 100%;

    ::v-deep {
      [data-identifier='role'],
      [data-identifier='actions'] {
        justify-content: center;
      }

      .column-actions {
        padding: 0;

        > button {
          color: $primary;
          padding: 4px;
          border-radius: 50%;
          outline: none;

          &:hover {
            background: $primary-20;
          }
        }

        &--dropdown {
          min-width: 140px;
          background-color: $gray-00;
          box-shadow: 4px 4px 16px rgba($lb-black, 0.25);
          position: absolute;
          top: 100%;
          right: 16px;
          z-index: 1;
          padding: 1em 0;

          &[drop-up] {
            top: auto;
            bottom: 100%;
          }

          button {
            padding: 0.5em 1.5em;
            cursor: pointer;
            width: 100%;
            text-align: left;

            &:hover {
              background-color: $gray-20;
            }
          }
        }
      }
    }
  }

  &-form {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .form-section {
      position: relative;
      margin-bottom: 24px;

      &.field--error {
        label {
          color: $danger;
        }

        .form-control {
          border-color: $danger;
          color: $danger;
        }

        .field--warning {
          display: block;
        }
      }
    }

    label {
      font-size: $size-small;
      font-weight: $lb-medium;
      color: $gray-70;
      margin-bottom: 0;
    }

    .field--warning {
      display: none;
      position: absolute;
      top: 100%;
      font-size: $size-smaller;
      color: $danger;
    }

    .form-control {
      height: auto;
      background-color: $gray-00;
      border: 1px solid $gray-30;
      color: $gray-100;
      border-radius: 4px;
      padding: 9px 12px;

      &::placeholder {
        color: $gray-30;
        font-size: $size-small;
      }

      &:focus {
        border-color: $primary;
      }
    }

    input[disabled] {
      background-color: $gray-20;
      color: $gray-70;
    }

    ::v-deep {
      .vs__dropdown-toggle {
        background-color: $gray-00;
        border: 1px solid $gray-30;
        border-radius: 4px;
        padding: 8px 12px; // 1px less than other uitypes since it adds a transparent 1px border
      }

      .vs__search,
      .vs__search:focus,
      .vs__selected-options {
        padding-left: 0;
      }

      .vs__search::placeholder,
      .vs__search::-webkit-input-placeholder {
        color: $gray-30;
        font-size: $size-small;
      }

      .vs__selected-options {
        max-width: calc(100% - 23px); // minus vs__actions width
        flex-wrap: nowrap;
      }

      .vs__search {
        margin-top: 0;
        line-height: 1.5;
      }

      .vs__selected {
        display: block;
        margin: 0;
        padding: 0;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.5;
      }

      .v-select {
        &.vs--open {
          .vs__dropdown-toggle {
            border-color: $primary;
          }
        }
      }

      .vs__dropdown-menu {
        max-height: 161px; // 26 * itens + 5
      }

      .vs__dropdown-option {
        padding-right: 10px;
        padding-left: 10px;
        overflow: clip;
        text-overflow: ellipsis;
      }

      .vs__dropdown-option--highlight {
        background: $primary;
      }

      .vs__actions {
        padding-top: 2px;
      }

      .vs__spinner {
        font-size: 4px;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-between;
      margin-top: 16px;

      button {
        min-width: 140px;
      }
    }
  }

  .edit-modal {
    position: fixed;
    top: 45px;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999;

    &__content {
      width: 400px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $gray-00;
      position: relative;
    }

    &__header {
      width: 100%;

      h2 {
        font-size: $size-bigger;
      }
    }

    .submit-feedback {
      position: absolute;
      top: 100%;
      right: 0;
      color: $danger;
    }
  }

  .remove-modal {
    position: fixed;
    top: 45px;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;

    &__content {
      width: 400px;
      display: flex;
      padding: 40px 50px;
      flex-direction: column;
      background-color: $gray-00;
      position: relative;
    }

    &__header {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      flex-direction: column;
      position: relative;

      h2 {
        font-size: $size-bigger;
        font-weight: $lb-medium;
        line-height: 35px;
        text-align: center;
        letter-spacing: -1px;
        color: #474747;
        margin: 28px 0 48px;
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      margin-bottom: 24px;

      p {
        font-size: $size-normal;
        line-height: 22px;
        color: #474747;
        margin-bottom: 16px;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        min-width: 140px;
      }
    }
  }

  .btn-close {
    position: absolute;
    right: 0;
    top: 0;
    padding: 12px;
    cursor: pointer;

    .icon {
      display: block;
      font-size: 22px;
    }
  }

  .icon-wrapper {
    margin-left: 8px;
    display: inline-flex;
    position: relative;
    vertical-align: middle;

    .icon {
      cursor: pointer;
    }

    .slb-tooltip {
      visibility: hidden;
      opacity: 0;
      margin-left: 0;
      width: 340px;
      text-align: left;

      strong {
        font-weight: 900 !important;
      }
    }

    &:hover {
      .slb-tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
    }
  }
}
</style>
